import React, { useState, useEffect } from 'react';
import { Typography, Card, Row, Col, Alert, Spin, Tag, Statistic } from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  StopOutlined,
  CloseCircleOutlined,
  RightOutlined,
  WalletOutlined,
  GlobalOutlined,
  ShopOutlined,
  FileTextOutlined,
  TeamOutlined,
  ReloadOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  ClockCircleFilled,
  SyncOutlined,
  DollarOutlined
} from '@ant-design/icons';
import styled from 'styled-components';
import { useDashboard } from '../../../context/DashboardContext';
import SessionTimer from '../../SessionTimer/SessionTimer';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import { useNavigate } from 'react-router-dom';
import SweepStatusDistributionChart from '../../Charts/SweepStatusDistributionChart';
import SweepTrendsChart from '../../Charts/SweepTrendsChart';
import WebhookStatsChart from '../../Charts/WebhookStatsChart';
import WalletTrendsChart from '../../Charts/WalletTrendsChart';
import CurrencyDistributionChart from '../../Charts/CurrencyDistributionChart';
import ActionItems from '../../Dashboard/ActionItems';

const { Title, Paragraph } = Typography;

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
};

const DashboardContainer = styled.div`
  padding: 0;

  @media (max-width: 768px) {
    padding: 8px;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 16px;
  }
`;

const DashboardTitle = styled(Title)`
  margin-bottom: 0 !important;
  color: ${COLORS.textPrimary} !important;
  font-size: 24px !important;

  @media (max-width: 768px) {
    font-size: 20px !important;
    text-align: center;
  }
`;

const TimerWrapper = styled.div`
  background: ${COLORS.bgSecondary};
  padding: 8px 16px;
  border-radius: 8px;
  border: 1px solid ${COLORS.border};
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    padding: 6px 12px;
    font-size: 12px;
  }
`;

const StatusCard = styled(Card)`
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  margin-bottom: 24px;

  .ant-card-body {
    padding: 24px !important;

    @media (max-width: 768px) {
      padding: 16px !important;
    }
  }

  @media (max-width: 768px) {
    margin-bottom: 16px;
  }
`;

const StatusBadge = styled.div<{ status: string }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 18px;
  font-weight: 700;
  background: ${props => {
    if (props.status === 'ACTIVE') return 'rgba(16, 185, 129, 0.1)';
    if (props.status === 'PENDING') return 'rgba(251, 191, 36, 0.1)';
    return 'rgba(239, 68, 68, 0.1)';
  }};
  color: ${props => {
    if (props.status === 'ACTIVE') return '#10B981';
    if (props.status === 'PENDING') return '#FBBF24';
    return '#EF4444';
  }};
  border: 2px solid ${props => {
    if (props.status === 'ACTIVE') return '#10B981';
    if (props.status === 'PENDING') return '#FBBF24';
    return '#EF4444';
  }};

  @media (max-width: 768px) {
    padding: 8px 16px;
    font-size: 14px;
    font-weight: 600;
  }
`;

const ValidationCard = styled(Card)`
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  margin-bottom: 24px;

  .ant-card-body {
    padding: 12px !important;
  }

  .ant-card-head {
    padding: 8px 12px !important;
    min-height: auto !important;
  }

  .ant-card-head-title {
    padding: 0 !important;
    font-size: 14px !important;
  }
`;

const ValidationItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid ${COLORS.border};
  
  &:last-child {
    border-bottom: none;
  }
  
  .item-info {
    flex: 1;
    min-width: 0;
    
    .item-label {
      font-weight: 500;
      color: ${COLORS.textPrimary};
      font-size: 13px;
      display: flex;
      align-items: center;
      gap: 4px;
      margin-bottom: 1px;
      line-height: 1.2;
    }
    
    .item-description {
      color: ${COLORS.textSecondary};
      font-size: 10px;
      margin-bottom: 0;
      line-height: 1.2;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .item-link {
    color: ${COLORS.accent};
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 3px;
    font-size: 11px;
    transition: all 0.2s;
    white-space: nowrap;
    margin-left: 8px;
    flex-shrink: 0;

    &:hover {
      color: ${COLORS.accent};
      opacity: 0.8;
    }
  }
`;

const StatCard = styled(Card)`
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  border-radius: 0.75rem !important;
  
  .ant-statistic-title {
    color: ${COLORS.textSecondary} !important;
    font-size: 14px;
  }
  
  .ant-statistic-content {
    color: ${COLORS.textPrimary} !important;
    font-weight: 700;
  }
  
  &.border-cyan { border-left: 4px solid #22D3EE !important; }
  &.border-purple { border-left: 4px solid #C084FC !important; }
  &.border-amber { border-left: 4px solid #FBBF24 !important; }
  &.border-red { border-left: 4px solid #F87171 !important; }
  &.border-green { border-left: 4px solid #10B981 !important; }
  &.border-orange { border-left: 4px solid #F59E0B !important; }
`;

const Dashboard: React.FC = () => {
  const { sessionExpiryTime, onSessionExpire } = useDashboard();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [companyData, setCompanyData] = useState<any>(null);
  const [validationData, setValidationData] = useState<any>(null);
  const [validationLoading, setValidationLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [stats, setStats] = useState<any>({
    totalWallets: 0,
    assignedWallets: 0,
    availableWallets: 0,
    pendingSweeps: 0,
    processingSweeps: 0,
    failedSweeps: 0,
    completedSweeps: 0,
    totalStaff: 0,
    supportedBlockchains: 0,
    supportedCurrencies: 0,
    supportedContractAddresses: 0,
    pendingInvoiceCount: 0,
    pendingInvoiceSum: 0,
  });

  useEffect(() => {
    fetchCompanyProfile();
    fetchDashboardStats();
  }, []);

  useEffect(() => {
    // Fetch validation data for all merchants to show configuration status
    if (companyData) {
      fetchValidation();
    }
  }, [companyData]);

  const fetchCompanyProfile = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(API_ENDPOINTS.MERCHANT_GET_COMPANY_PROFILE);
      
      if (response.data?.status === 'success' && response.data?.data?.profile) {
        setCompanyData(response.data.data.profile);
      }
    } catch (error: any) {
      console.error('Error fetching company profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchValidation = async () => {
    try {
      setValidationLoading(true);
      const response = await apiClient.get(API_ENDPOINTS.MERCHANT_VALIDATE_INTEGRATION);
      
      if (response.data?.status === 'success' && response.data?.data) {
        setValidationData(response.data.data);
      }
    } catch (error: any) {
      console.error('Error fetching validation:', error);
    } finally {
      setValidationLoading(false);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      setStatsLoading(true);
      const response = await apiClient.get(API_ENDPOINTS.MERCHANT_GET_DASHBOARD_STATS);
      if (response.data?.status === 'success' && response.data?.data) {
        setStats(response.data.data);
      }
    } catch (error: any) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return <CheckCircleOutlined />;
      case 'PENDING':
        return <ClockCircleOutlined />;
      case 'SUSPENDED':
        return <StopOutlined />;
      default:
        return null;
    }
  };

  const getStatusMessage = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'Your merchant account is active and ready to use.';
      case 'PENDING':
        return 'Your merchant account is pending activation. Please complete your profile setup.';
      case 'SUSPENDED':
        return 'Your merchant account has been suspended. Please contact support.';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <DashboardContainer>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
          <Spin size="large" />
        </div>
      </DashboardContainer>
    );
  }

  // Check both merchantStatus and status - if either is PENDING, show pending message
  const merchantStatus = companyData?.merchantStatus || 'PENDING';
  const companyStatus = companyData?.status || 'ACTIVE';
  const isPending = merchantStatus === 'PENDING' || companyStatus === 'PENDING';

  return (
    <DashboardContainer>
      <HeaderRow>
        <DashboardTitle level={2}>Merchant Dashboard</DashboardTitle>
        {sessionExpiryTime && (
          <TimerWrapper>
            <span style={{ color: COLORS.textSecondary, fontSize: '14px', fontWeight: 500 }}>Session Expires:</span>
            <SessionTimer expiryTime={sessionExpiryTime} onExpire={onSessionExpire} />
          </TimerWrapper>
        )}
      </HeaderRow>

      <StatusCard>
        <Row align="middle" justify="space-between">
          <Col>
            <div style={{ marginBottom: 8 }}>
              <Typography.Text style={{ color: COLORS.textSecondary, fontSize: '14px' }}>
                Merchant Account Status
              </Typography.Text>
            </div>
            <StatusBadge status={isPending ? 'PENDING' : merchantStatus}>
              {getStatusIcon(isPending ? 'PENDING' : merchantStatus)}
              {isPending ? 'PENDING' : merchantStatus}
            </StatusBadge>
          </Col>
          <Col>
            <Paragraph style={{ color: COLORS.textPrimary, margin: 0, maxWidth: '400px' }}>
              {getStatusMessage(isPending ? 'PENDING' : merchantStatus)}
            </Paragraph>
          </Col>
        </Row>
      </StatusCard>

      {/* Show validation section only when merchant is PENDING - once admin approves, only show on Settings page */}
      {isPending && validationLoading ? (
        <ValidationCard>
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
            <Spin size="large" />
          </div>
        </ValidationCard>
      ) : isPending && validationData?.items ? (
        <ValidationCard
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: '14px' }}>
                Integration Validation
              </span>
              {validationData.allComplete && (
                <Tag color="success" icon={<CheckCircleOutlined />} style={{ margin: 0, fontSize: '10px', padding: '1px 6px', lineHeight: '18px' }}>
                  All Complete
                </Tag>
              )}
            </div>
          }
        >
          <Alert
            message="Account Setup Required"
            description="Complete your merchant configuration to get activated."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          {validationData.items.map((item: any) => (
            <ValidationItem key={item.key}>
              <div className="item-info">
                <div className="item-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {item.label}
                </div>
                <div className="item-description">
                  {item.description}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                {item.isComplete ? (
                  <Tag color="success" icon={<CheckCircleOutlined />} style={{ margin: 0, fontSize: '10px', padding: '1px 6px', lineHeight: '18px' }}>
                    Complete
                  </Tag>
                ) : (
                  <Tag color="error" icon={<CloseCircleOutlined />} style={{ margin: 0, fontSize: '10px', padding: '1px 6px', lineHeight: '18px' }}>
                    Incomplete
                  </Tag>
                )}
                <span
                  className="item-link"
                  onClick={() => navigate(item.link)}
                >
                  Configure <RightOutlined style={{ fontSize: '9px' }} />
                </span>
              </div>
            </ValidationItem>
          ))}
        </ValidationCard>
      ) : null}

      {merchantStatus === 'SUSPENDED' && (
        <Alert
          message="Account Suspended"
          description="Your merchant account has been suspended. Please contact support for assistance."
          type="error"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      {/* Dashboard Statistics */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={4} style={{ color: COLORS.textPrimary, marginBottom: 0 }}>
            Dashboard Statistics
          </Title>
          <button
            onClick={fetchDashboardStats}
            disabled={statsLoading}
            style={{
              background: 'transparent',
              border: `1px solid ${COLORS.border}`,
              color: COLORS.textPrimary,
              padding: '4px 12px',
              borderRadius: '4px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <ReloadOutlined spin={statsLoading} />
            Refresh
          </button>
        </div>
          
          {statsLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
              <Spin size="large" />
            </div>
          ) : (
            <Row gutter={[16, 16]}>
              {/* Wallet Statistics */}
              <Col xs={24} sm={12} lg={6}>
                <StatCard className="border-cyan">
                  <Statistic 
                    title="Total Wallets" 
                    value={stats.totalWallets || 0} 
                    prefix={<WalletOutlined />} 
                    valueStyle={{ color: COLORS.textPrimary }}
                  />
                </StatCard>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <StatCard className="border-green">
                  <Statistic 
                    title="Assigned Wallets" 
                    value={stats.assignedWallets || 0} 
                    prefix={<CheckCircleOutlined />} 
                    valueStyle={{ color: COLORS.textPrimary }}
                  />
                </StatCard>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <StatCard className="border-amber">
                  <Statistic 
                    title="Available Wallets" 
                    value={stats.availableWallets || 0} 
                    prefix={<WalletOutlined />} 
                    valueStyle={{ color: COLORS.textPrimary }}
                  />
                </StatCard>
              </Col>
              
              {/* Sweep Statistics */}
              <Col xs={24} sm={12} lg={6}>
                <StatCard className="border-orange">
                  <Statistic 
                    title="Pending Sweeps" 
                    value={stats.pendingSweeps || 0} 
                    prefix={<ClockCircleFilled />} 
                    valueStyle={{ color: COLORS.textPrimary }}
                  />
                </StatCard>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <StatCard className="border-purple">
                  <Statistic 
                    title="Processing Sweeps" 
                    value={stats.processingSweeps || 0} 
                    prefix={<SyncOutlined spin />} 
                    valueStyle={{ color: COLORS.textPrimary }}
                  />
                </StatCard>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <StatCard className="border-red">
                  <Statistic 
                    title="Failed Sweeps" 
                    value={stats.failedSweeps || 0} 
                    prefix={<CloseCircleFilled />} 
                    valueStyle={{ color: COLORS.textPrimary }}
                  />
                </StatCard>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <StatCard className="border-green">
                  <Statistic 
                    title="Completed Sweeps" 
                    value={stats.completedSweeps || 0} 
                    prefix={<CheckCircleFilled />} 
                    valueStyle={{ color: COLORS.textPrimary }}
                  />
                </StatCard>
              </Col>
              
              {/* Resource Statistics */}
              <Col xs={24} sm={12} lg={6}>
                <StatCard className="border-cyan">
                  <Statistic 
                    title="Staff Members" 
                    value={stats.totalStaff || 0} 
                    prefix={<TeamOutlined />} 
                    valueStyle={{ color: COLORS.textPrimary }}
                  />
                </StatCard>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <StatCard className="border-purple">
                  <Statistic 
                    title="Supported Blockchains" 
                    value={stats.supportedBlockchains || 0} 
                    prefix={<GlobalOutlined />} 
                    valueStyle={{ color: COLORS.textPrimary }}
                  />
                </StatCard>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <StatCard className="border-amber">
                  <Statistic 
                    title="Supported Currencies" 
                    value={stats.supportedCurrencies || 0} 
                    prefix={<ShopOutlined />} 
                    valueStyle={{ color: COLORS.textPrimary }}
                  />
                </StatCard>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <StatCard className="border-cyan">
                  <Statistic 
                    title="Supported Contracts" 
                    value={stats.supportedContractAddresses || 0} 
                    prefix={<FileTextOutlined />} 
                    valueStyle={{ color: COLORS.textPrimary }}
                  />
                </StatCard>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <StatCard className="border-orange">
                  <Statistic 
                    title="Pending Invoices" 
                    value={stats.pendingInvoiceCount || 0} 
                    prefix={<DollarOutlined />} 
                    valueStyle={{ color: COLORS.textPrimary }}
                  />
                </StatCard>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <StatCard className="border-red">
                  <Statistic 
                    title="Unpaid Amount" 
                    value={stats.pendingInvoiceSum || 0} 
                    prefix="$" 
                    precision={2}
                    valueStyle={{ color: COLORS.textPrimary }}
                  />
                </StatCard>
              </Col>
            </Row>
          )}
        </div>

      {/* Action Items */}
      <ActionItems isAdmin={false} />

      {/* Charts Section */}
      <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
        <Col xs={24} lg={12}>
          <SweepStatusDistributionChart />
        </Col>
        <Col xs={24} lg={12}>
          <CurrencyDistributionChart />
        </Col>
        <Col xs={24}>
          <SweepTrendsChart isAdmin={false} />
        </Col>
        <Col xs={24}>
          <WebhookStatsChart isAdmin={false} />
        </Col>
        <Col xs={24}>
          <WalletTrendsChart />
        </Col>
      </Row>

      <Card
        style={{
          background: COLORS.bgSecondary,
          border: `1px solid ${COLORS.border}`,
          marginTop: 24,
        }}
      >
        <Title level={4} style={{ color: COLORS.textPrimary, marginBottom: 16 }}>
          Welcome to Switch Wallet Merchant Dashboard
        </Title>
        <Paragraph style={{ color: COLORS.textSecondary }}>
          Manage your company profile, staff, API keys, and view your supported blockchains and currencies.
          Use the sidebar to navigate through different sections.
        </Paragraph>
      </Card>
    </DashboardContainer>
  );
};

export default Dashboard;

